import { MAX_UPLOAD_BYTES } from "@ncp/api-client/uploadLimits";
import type { AttachmentUploadResult, UploadedAttachment } from "./types";

/** 200 MB per file (3 Nov 2026; 25 MB before), the server's own ceiling; files over 20 MB travel in parts (attachmentParts.ts). */
export const MAX_ATTACHMENT_BYTES = MAX_UPLOAD_BYTES;
export const MAX_ATTACHMENTS = 5;
export type SelectedAttachment = {
  id: string;
  file: File;
  status: "queued" | "uploading" | "failed";
  error?: "tooLarge" | "empty" | "interrupted" | "rejected";
  retryable: boolean;
};
type Snapshot = { selected: SelectedAttachment[]; attachments: UploadedAttachment[] };

/** One file per request; a failed POST is retried only by an explicit user action. */
export class AttachmentUploadQueue {
  private state: Snapshot = { selected: [], attachments: [] };
  private listeners = new Set<() => void>();
  private sequence = 0;
  private running = false;
  private active = true;
  constructor(private readonly upload: (file: File) => Promise<AttachmentUploadResult>) {}
  snapshot = () => this.state;
  subscribe = (listener: () => void) => { this.listeners.add(listener); return () => { this.listeners.delete(listener); }; };
  private update(state: Snapshot) { this.state = state; for (const listener of this.listeners) listener(); }
  activate() { this.active = true; void this.pump(); }
  deactivate() { this.active = false; }
  /** Returns how many selections did not fit; existing uploads are never replaced. */
  add(files: File[]) {
    const available = Math.max(0, MAX_ATTACHMENTS - this.state.attachments.length - this.state.selected.length);
    const selected = files.slice(0, available).map((file): SelectedAttachment => {
      const error = file.size > MAX_ATTACHMENT_BYTES ? "tooLarge" : file.size === 0 ? "empty" : undefined;
      return { id: `attachment-${++this.sequence}`, file, status: error ? "failed" : "queued", error, retryable: !error };
    });
    this.update({ ...this.state, selected: [...this.state.selected, ...selected] });
    void this.pump();
    return Math.max(0, files.length - available);
  }
  retry(id: string) {
    const selected = this.state.selected.find((f) => f.id === id);
    if (selected?.status !== "failed" || !selected.retryable) return;
    this.update({ ...this.state, selected: this.state.selected.map((f) => f.id === id ? { ...f, status: "queued", error: undefined } : f) });
    void this.pump();
  }
  removeSelected(id: string) {
    this.update({ ...this.state, selected: this.state.selected.filter((f) => f.id !== id || f.status === "uploading") });
  }
  removeAttachment(id: string) { this.update({ ...this.state, attachments: this.state.attachments.filter((f) => f.documentId !== id) }); }
  consumeAttachments(ids: Iterable<string>) {
    const sent = new Set(ids);
    if (sent.size === 0) return;
    this.update({ ...this.state, attachments: this.state.attachments.filter((f) => !sent.has(f.documentId)) });
  }
  private async pump() {
    if (this.running || !this.active) return;
    this.running = true;
    try {
      while (this.active) {
        const item = this.state.selected.find((f) => f.status === "queued");
        if (!item) break;
        this.update({ ...this.state, selected: this.state.selected.map((f) => f.id === item.id ? { ...f, status: "uploading" } : f) });
        try {
          const result = await this.upload(item.file);
          if (result.attachments.length === 0) { this.fail(item.id, "rejected", true); continue; }
          // Reconcile the returned identifiers before another file or retry can start.
          const existing = new Set(this.state.attachments.map((a) => a.documentId));
          this.update({ selected: this.state.selected.filter((f) => f.id !== item.id), attachments: [...this.state.attachments, ...result.attachments.filter((a) => !existing.has(a.documentId))] });
        } catch (error) {
          const status = error && typeof error === "object" && "status" in error ? error.status : undefined;
          const rejected = status === 400 || status === 413 || status === 415 || status === 422;
          this.fail(item.id, status === 413 ? "tooLarge" : rejected ? "rejected" : "interrupted", status !== 413 && status !== 415);
        }
      }
    } finally { this.running = false; }
  }
  private fail(id: string, error: SelectedAttachment["error"], retryable: boolean) {
    this.update({ ...this.state, selected: this.state.selected.map((f) => f.id === id ? { ...f, status: "failed", error, retryable } : f) });
  }
}
